import * as React from 'react';
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Checkbox, FormControlLabel, Stack, Tooltip } from '@mui/material';
import { DraggableDialogPaper } from "../../../screens/positions-and-orders/dialogs/DraggableDialogPaper";
import { DraggableDialogTitle } from "../../../screens/positions-and-orders/dialogs/DraggableDialogTitle";
import { PortfolioGroupAddOrUpdateModel } from "../types/PortfolioGroupAddOrUpdateModel";
import { ActionConfirmationDialog } from "../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog";
import { useActionConfirmationDialog } from "../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import {
    usePortfolioManagement,
} from "../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";

export function PortfolioGroupManagerDialog({
    isOpen,
    setIsOpen,
    isCreation,
    submitAction,
    editingPortfolioGroupModel,
    icon,
}: {
    isOpen: boolean,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    isCreation: boolean,
    submitAction: ((portfolioGroup: PortfolioGroupAddOrUpdateModel) => void) | undefined,
    editingPortfolioGroupModel?: PortfolioGroupAddOrUpdateModel | null,
    icon: React.ReactNode;
}) {
    const {
        deleteSelectedPortfolioGroup,
    } = usePortfolioManagement();

    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        setConfirmedAction,
        confirmationMessage,
        setConfirmationMessage,
        submitButtonName,
        setSubmitButtonName,
    } = useActionConfirmationDialog();

    const [name, setName] = useState<string>('');
    const [usePopupForStepChangeSelector, setUsePopupForStepChangeSelector] = useState<boolean>(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setName(isCreation ? '' : editingPortfolioGroupModel?.name ?? '');
        setUsePopupForStepChangeSelector(isCreation
            ? false
            : editingPortfolioGroupModel?.usePopupForStepChangeSelector ?? false);
    }, [editingPortfolioGroupModel, isCreation, isOpen]);

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const portfolioGroupModel = {
            name: name.trim(),
            usePopupForStepChangeSelector,
        };

        if (portfolioGroupModel.name.length === 0) {
            return;
        }

        if (submitAction) {
            submitAction(portfolioGroupModel);
        }

        handleClose();
    };

    function getDialogTitle() {
        return isCreation
            ? 'Create portfolio group'
            : 'Edit portfolio group';
    }

    function getSubmitButtonCaption() {
        return isCreation
            ? 'Create'
            : 'Update';
    }

    const handleForceDelete = () => {
        setConfirmedAction(() => deleteSelectedPortfolioGroup);
        setConfirmationMessage("Are you sure you want to force delete the portfolio group?");
        setSubmitButtonName("Delete");
        setIsActionConfirmationDialogOpen(true);
    }

    return (
        <>
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                confirmedParentAction={confirmedAction}
                submitButtonName={submitButtonName}
                doAfterConfirmation={handleClose}
            />
            <Dialog
                open={isOpen}
                onClose={handleClose}
                PaperComponent={DraggableDialogPaper}
                PaperProps={{
                    component: 'form',
                    onSubmit: handleSubmit,
                }}
            >
                <DraggableDialogTitle
                    icon={icon}
                    title={getDialogTitle()}
                    onClose={handleClose}
                />
                <DialogContent>
                    <Stack
                        direction="column"
                        spacing={2}
                        alignItems="flex-start"
                        sx={{ width: 360 }}
                    >
                        <TextField
                            autoFocus
                            required
                            id="portfolioGroupName"
                            name="portfolioGroupName"
                            label="Name"
                            type="text"
                            variant="standard"
                            size="small"
                            value={name}
                            inputProps={{ maxLength: 50 }}
                            onChange={event => setName(event.target.value)}
                            sx={{
                                width: 360,
                                margin: 0,
                                padding: 0,
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    sx={{ padding: 0, marginLeft: -0.5, marginRight: 0.5 }}
                                    color="default"
                                    id="portfolioGroupUsePopupForStepChangeSelector"
                                    name="usePopupForStepChangeSelector"
                                    checked={usePopupForStepChangeSelector}
                                    onChange={event => setUsePopupForStepChangeSelector(event.target.checked)}
                                />
                            }
                            label="Use popup for step change selector"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        size="small"
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={name.trim().length === 0}
                    >
                        {getSubmitButtonCaption()}
                    </Button>
                    {!isCreation &&
                        <Tooltip
                            arrow
                            title="Delete the portfolio group"
                            slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                        >
                            <Button
                                sx={{ fontWeight: "bold" }}
                                size="small"
                                variant="contained"
                                color="error"
                                onClick={handleForceDelete}
                            >
                                Force delete
                            </Button>
                        </Tooltip>
                    }
                </DialogActions>
            </Dialog>
        </>
    );
}
